// Design §10 step 7 — pure, framework-free decision functions extracted
// out of App.jsx so they can be unit-tested with plain node (no CRA/
// jest build needed — this repo has no client test harness wired up;
// see test_client_step7_pure.js at the repo root, run with
// `node test_client_step7_pure.js`). Both functions are deterministic:
// given the same inputs they always return the same output, with no
// DOM/React/fetch access, so App.jsx's own call sites stay free to pass
// in whatever derived values they already compute.
//
// CommonJS (module.exports) on purpose, not ESM export — CRA's webpack/
// babel pipeline interops `import { x } from './step7Pure'` against a
// CommonJS module.exports fine, and this form is also directly
// `require()`-able from plain node for the test file above, with no
// build step and no transpilation. No 'use strict' pragma — the client
// build flags it (this file is loaded in an ES module context there),
// and removing it changes nothing here: there's no sloppy-mode-only
// construct in this file, and plain `require()` from the node test
// below works identically either way.
// Decided, verbatim copy — must never be altered by either caller.
// Copy change (branch claude/step7-copy): the prior strings discouraged
// rating and omitted something true — every rating already feeds the
// recommendation prompt (buildPrompt's effectiveRatedWines loved/
// neutral/disliked lists, scoreCatalogWines' producer affinity)
// regardless of verification; only the palate-slider effect depends on
// it. The decision logic below (decideRatingToast's cases, the moved/
// ratingMovedProfile gating, the flag-off fallthrough) is unchanged.
const PENDING_COPY = "Saved. We'll use this in your recommendations, and it'll tune your palate profile once we've confirmed this wine.";
const TERMINAL_COPY = "Saved. We'll use this rating in your recommendations.";

// decideRatingToast(response, stars) — the step7-specific half of what
// used to be maybeShowRatingToast's only job. Returns one of:
//   { type: 'none' }         — show nothing (rating<=0, or an eligible-
//                               but-didn't-move case like a 3★ anchor on
//                               an otherwise-eligible wine)
//   { type: 'message', text }— show this exact text (pending/terminal)
//   { type: 'fallthrough' }  — the step7 fields are absent (flag off) OR
//                               present with eligibilityCase 'moved' AND
//                               ratingMovedProfile true — in both cases
//                               the CALLER's existing drift/new-wine
//                               logic (computeToastMessage / isNewRatedWine,
//                               both of which close over component state
//                               and so are NOT moved here) decides what,
//                               if anything, to show.
//
// A response with no eligibilityCase key at all (flag off — the server
// omits the key entirely in that case, it is never present-but-undefined
// by the server's own contract) always falls through, so a flag-off
// response produces EXACTLY today's behavior, unchanged.
function decideRatingToast(response, stars) {
  if (!stars || stars <= 0) return { type: 'none' };
  const hasStep7Fields = !!response && typeof response.eligibilityCase === 'string';
  if (!hasStep7Fields) return { type: 'fallthrough' };
  if (response.eligibilityCase === 'pending') return { type: 'message', text: PENDING_COPY };
  if (response.eligibilityCase === 'terminal') return { type: 'message', text: TERMINAL_COPY };
  // eligibilityCase === 'moved'
  if (!response.ratingMovedProfile) return { type: 'none' }; // e.g. a confirmed, eligible 3★ anchor — moves nothing; no copy exists for this, by design
  return { type: 'fallthrough' };
}

// isChangeUnseen — the one shared "was this change ever seen" rule,
// used by both shouldShowProfileBadge AND (this round, item 2) the
// Profile tab's cause-banner visibility, so they can never silently
// drift apart. True when profileChangedAt is strictly after
// profileLastSeenChangeAt, or there is no last-seen timestamp at all
// (never seen). A missing profileChangedAt is treated as "no change",
// never "unseen".
function isChangeUnseen(profileChangedAt, profileLastSeenChangeAt) {
  if (!profileChangedAt) return false;
  const changedAtMs = new Date(profileChangedAt).getTime();
  const seenAtMs = profileLastSeenChangeAt ? new Date(profileLastSeenChangeAt).getTime() : 0;
  return changedAtMs > seenAtMs;
}

// shouldShowProfileBadge — the decided rule (prior round): when the
// step7 server fields are present on the profile row (flagOn), the
// badge is driven ONLY by profile_change_reason + isChangeUnseen —
// the legacy palateUpdateSeen/drift path must NOT run at all in that
// case (silent drift, reason===null, shows nothing even if
// palateUpdateSeen is false and a dimension drifted). When flagOn is
// false (the three fields are simply absent from the server response),
// today's exact legacy logic runs, unchanged. pendingArchetypeReveal
// always wins either way, exactly as today.
function shouldShowProfileBadge({
  pendingArchetypeReveal,
  flagOn,
  profileChangeReason,
  profileChangedAt,
  profileLastSeenChangeAt,
  palateUpdateSeen,
  anyDimensionDrifted,
}) {
  if (pendingArchetypeReveal) return true;
  if (flagOn) {
    if (profileChangeReason == null) return false; // silent drift (or nothing) — never shown
    return isChangeUnseen(profileChangedAt, profileLastSeenChangeAt);
  }
  if (palateUpdateSeen) return false;
  return !!anyDimensionDrifted;
}

// shouldShowCauseBannerOnEntry — item 2, this round: the cause banner
// must NOT be permanent. It is evaluated ONCE, at the moment the user
// enters the Profile tab (using isChangeUnseen against whatever
// profileChangedAt/profileLastSeenChangeAt were true at that instant,
// BEFORE the caller marks the notice seen) — not re-derived on every
// render from current state, which would always read a reason that's
// still non-null and show the banner forever. The caller captures this
// function's result into its own state and keeps rendering the banner
// from that captured value for the rest of the visit, even after it
// calls POST /api/profile/seen-change and updates
// profileLastSeenChangeAt (which would otherwise flip isChangeUnseen to
// false mid-visit and yank the banner out from under the user).
function shouldShowCauseBannerOnEntry({ flagOn, profileChangeReason, profileChangedAt, profileLastSeenChangeAt }) {
  if (!flagOn) return false;
  if (profileChangeReason == null) return false;
  return isChangeUnseen(profileChangedAt, profileLastSeenChangeAt);
}

module.exports = { decideRatingToast, shouldShowProfileBadge, shouldShowCauseBannerOnEntry, isChangeUnseen, PENDING_COPY, TERMINAL_COPY };
